import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { ProjectUpdate } from "../api/types";
import { data, onDataChange } from "../data";
import "./Notifications.css";

/** The bell in the sidebar footer. Opens a panel of recent project updates,
 *  which is the same feed the dashboard shows. Unread state is local for now;
 *  it needs per-user seen state on the backend to survive a reload. */

function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "Yesterday" : `${days}d ago`;
}

/** What is worth interrupting somebody for.
 *
 *  Two kinds. Things needing a decision, a field the extraction was unsure of
 *  or two sources disagreeing. And a call somebody logged, because the point
 *  of logging it is that everyone following the project finds out; a
 *  notification nobody can see is not one.
 *
 *  A grid moving a project from development to casting is worth knowing and
 *  not worth stopping for, so it stays out. A new role a client could be up
 *  for is in: it is an opportunity to act on, the same shape as a call, and
 *  losing it in the feed is losing the point of surfacing it. A field change
 *  stays out, worth seeing on the dashboard but not worth the bell. */
const NEEDS_ACTION = new Set(["needs_review", "conflict", "call_logged", "new_role"]);

export default function Notifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ProjectUpdate[]>([]);
  const [seen, setSeen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = () => data.listUpdates().then(setItems);
    void load();
    // A call logged from the assistant has to reach the bell, or "everyone
    // following this project has been told" is only true in the database.
    return onDataChange(() => {
      setSeen(false);
      void load();
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unread = seen ? 0 : items.filter((u) => NEEDS_ACTION.has(u.kind)).length;

  return (
    <div className="notif" ref={wrap}>
      <button
        className="bell"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path
            d="M4.5 7a4.5 4.5 0 1 1 9 0c0 3 1.2 4.2 1.2 4.2H3.3S4.5 10 4.5 7Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path
            d="M7.3 13.8a1.9 1.9 0 0 0 3.4 0"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
        {unread > 0 && <span className="bell__dot">{unread}</span>}
      </button>

      {open && (
        <div className="notif__panel" role="dialog" aria-label="Notifications">
          <header className="notif__head">
            <h4>Notifications</h4>
            {unread > 0 && (
              <button type="button" onClick={() => setSeen(true)}>
                Mark all as read
              </button>
            )}
          </header>

          {items.length === 0 ? (
            <p className="notif__empty">Nothing new.</p>
          ) : (
            <ul className="notif__list">
              {items.map((u) => (
                <li key={u.id} className={NEEDS_ACTION.has(u.kind) && !seen ? "is-unread" : ""}>
                  <span className={`notif__dot notif__dot--${u.kind}`} aria-hidden="true" />
                  <span className="notif__body">
                    <span>{u.body}</span>
                    <span className="caption">{ago(u.occurred_at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <footer className="notif__foot">
            <Link to="/" onClick={() => setOpen(false)}>
              See all updates on the dashboard
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
